import { View, Text, StyleSheet, Linking } from 'react-native'
import { router } from 'expo-router'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Button } from '@/src/common/components/Button'
import { DesignSystem } from '@/src/design-system'
import { useState, useEffect, useMemo } from 'react'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { usePoints } from '@/src/common/hooks/usePoints'
import ExchangeIcon from '@/src/assets/icons/Exchange'
import { DropdownComponent } from '@/src/common/components/Dropdown'
import * as Stores from '../stores'
import * as Statsig from '../statsig'
import * as Navigation from '../navigation'

type PointOptionWithDisplay = Statsig.DynamicConfig.PointOption & {
  displayLabel: string
  valueKey: string
}

export function TransferConfirmationScreen() {
  const Alert = useAlert(TransferConfirmationScreen.name)
  const [selectedOption, setSelectedOption] = useState<PointOptionWithDisplay | null>(null)

  const { setMesaPointsToRedeem, setPartnerCredits, setConversionRate, setRemainingPoints } =
    Stores.usePointsToOmniStore()

  const { dynamicConfig, loadingStatus } =
    Statsig.DynamicConfig.useOmniPointsTransferDynamicConfig()

  const pointsQuery = usePoints()
  const pointsBalance =
    pointsQuery.initialized && pointsQuery.query.data
      ? pointsQuery.query.data.getPointsBalance.active.value
      : 0

  const queryLoading = !pointsQuery.initialized || pointsQuery.query.loading

  const parseResult = useMemo(() => {
    if (loadingStatus === 'Ready') {
      return Statsig.DynamicConfig.OmniPointsTransferSchema.safeParse(dynamicConfig.value)
    }
    return null
  }, [loadingStatus, dynamicConfig])

  // Android dropdown requires valueField to be a string, not a number
  // Add string versions for dropdown internal use while preserving numeric values for business logic
  const pointOptions = useMemo(
    () =>
      parseResult?.success
        ? parseResult.data.pointsOptions.map((option) => ({
            ...option,
            displayLabel: option.mesaPoints.toLocaleString(),
            valueKey: option.mesaPoints.toString(),
          }))
        : [],
    [parseResult],
  )

  useEffect(() => {
    if (pointOptions.length > 0 && !selectedOption) {
      const defaultOption = pointOptions[0]
      if (defaultOption) {
        setSelectedOption(defaultOption)
      }
    }
  }, [pointOptions, selectedOption])

  if (loadingStatus !== 'Ready') {
    return null
  }

  if (!parseResult?.success) {
    throw new Error(
      `Invalid Omni Points Transfer config: ${parseResult ? parseResult.error.message : 'No config data'}`,
    )
  }

  const handleContinue = () => {
    if (queryLoading) {
      Alert.alert('Card information is loading', 'Please wait a moment and try again.')
      return
    }

    if (!selectedOption) {
      Alert.alert('Select Amount', 'Please select the amount of points you want to transfer.')
      return
    }

    if (pointsBalance < selectedOption.mesaPoints) {
      Alert.alert(
        'Insufficient Mesa Points',
        'You do not have the required number of Mesa Points for the selected amount. Please select a different amount.',
      )
      return
    }

    const conversionRate = selectedOption.credits / selectedOption.mesaPoints

    setMesaPointsToRedeem(selectedOption.mesaPoints)
    setPartnerCredits(selectedOption.credits)
    setConversionRate(conversionRate)
    setRemainingPoints(pointsBalance - selectedOption.mesaPoints)

    router.push({
      pathname: Navigation.Routes.RedemptionConfirmation.route,
    })
  }

  const conversionText = pointOptions[0]
    ? `${pointOptions[0].mesaPoints.toLocaleString()} Mesa Points = ${pointOptions[0].credits.toLocaleString()} Omni Credits`
    : '30,000 Mesa Points = 100 Omni Credits'

  return (
    <StandaloneWrapper
      screen={Navigation.Routes.TransferConfirmation.route}
      title="Points Transfer"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <View style={styles.container}>
        <DesignSystem.Card style={styles.balanceCard}>
          <View style={styles.pointsContainer}>
            <Text style={[typography['label/sm-bold'], styles.balanceLabel]}>Mesa Points</Text>
            <Text style={typography['heading/heading1']}>{pointsBalance.toLocaleString()}</Text>
          </View>
          <View style={styles.conversionRow}>
            <Text style={styles.conversionLabel}>Conversion Rate</Text>
            <Text style={[typography['label/sm-bold'], styles.conversionValue]}>
              {conversionText}
            </Text>
          </View>
        </DesignSystem.Card>

        <DesignSystem.Card style={styles.selectionCard}>
          <View style={styles.selectionHeader}>
            <Text style={typography['label/sm-bold']}>Select Points Transfer Amount</Text>
          </View>

          <View style={styles.exchangeRow}>
            <View style={styles.inputContainer}>
              <Text style={[typography['label/sm-bold'], styles.inputLabel]}>Mesa Points</Text>
              <DropdownComponent
                style={styles.selectInput}
                placeholderStyle={styles.selectText}
                selectedTextStyle={styles.selectText}
                data={pointOptions}
                labelField="displayLabel"
                valueField="valueKey"
                value={selectedOption}
                onChange={setSelectedOption}
                placeholder="Select amount"
                renderItem={(item) => (
                  <View style={{ padding: 12 }}>
                    <Text style={typography['label/md-regular']}>{item.displayLabel}</Text>
                  </View>
                )}
                renderLeftIcon={() => null}
              />
            </View>

            <View style={styles.exchangeIcon}>
              <ExchangeIcon color={mesaColorNames['semantic/fg/base #131214']} />
            </View>

            <View style={styles.inputContainer}>
              <Text style={[typography['label/sm-bold'], styles.inputLabel]}>Omni Credits</Text>
              <View style={styles.displayInput}>
                <Text style={[typography['label/md-regular'], styles.displayText]}>
                  {selectedOption?.credits.toLocaleString() || '0'}
                </Text>
              </View>
            </View>
          </View>

          <Text
            style={[typography['label/sm-regular'], styles.termsLink]}
            onPress={() =>
              void Linking.openURL(
                'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
              )
            }
          >
            Terms and conditions
          </Text>
        </DesignSystem.Card>

        {/* How does this work? */}
        <DesignSystem.Card style={{ padding: 16, borderRadius: 12 }}>
          <DesignSystem.Accordion trigger="How does this work?">
            <DesignSystem.Markdown
              style={{
                body: {
                  ...typography['paragraph/sm'],
                  color: mesaColorNames['Mesa - Brand/Blackish #222222'],
                },
              }}
              children={
                'You can transfer Mesa Points to partners which you have connected and validated your accounts with. To perform a transfer, first link your account, then select the amount of Mesa Points which you would like to transfer. Points transfer is instant and not reversible. If you have issues please reach out to support@mesamember.com or to the partner which the Mesa Points were transferred to.'
              }
            />
          </DesignSystem.Accordion>
        </DesignSystem.Card>
      </View>

      <Button
        title="Continue"
        onPress={handleContinue}
        disabled={!selectedOption || queryLoading || pointsBalance === 0}
        style={{ marginBottom: 40 }}
      />
    </StandaloneWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 24,
  },
  balanceCard: {
    borderRadius: 12,
    padding: 16,
    gap: 24,
  },
  pointsContainer: {
    gap: 8,
  },
  balanceLabel: {
    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
  },
  conversionRow: {
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  conversionLabel: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  conversionValue: {
    flexShrink: 1,
    textAlign: 'right',
  },
  selectionCard: {
    borderRadius: 12,
    padding: 16,
    gap: 20,
  },
  selectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  exchangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  inputContainer: {
    flex: 1,
    gap: 8,
    position: 'relative',
  },
  inputLabel: {
    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
  },
  selectInput: {
    borderWidth: 1,
    borderColor: mesaColorNames['global/grey/20 #E6E9EB'],
    borderRadius: 8,
    padding: 12,
    minHeight: 48,
    justifyContent: 'center',
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  selectText: {
    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
    marginLeft: 0,
  },
  displayInput: {
    borderWidth: 1,
    borderColor: mesaColorNames['global/grey/20 #E6E9EB'],
    borderRadius: 8,
    padding: 12,
    minHeight: 48,
    justifyContent: 'center',
    backgroundColor: '#F4F6F7',
  },
  displayText: {
    color: mesaColorNames['global/grey/60 #6E7375'],
  },
  exchangeIcon: {
    marginTop: 24,
  },
  termsLink: {
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
})
